import { html, TemplateResult } from 'lit';
import { query, customElement } from 'lit/decorators.js';
import { WEB_COMPONENT_POLICY_PANEL_APPLICATION_CONTROL_RULES_FORM } from '../../../../../../../../webcomponents.constants';
import { translateFn } from '../../../../../../../../utils/translator.helper';
import { styles } from '../view/styles';
import { GzFormFieldWebComponent } from '../../../../../../../../ui/gzForm/formField';
import {
  ApplicationControlRuleAction,
  ApplicationControlRuleTargetsAppInventory,
  ApplicationControlRuleTargetsProcesses,
  ApplicationControlRuleTargetsType,
  ApplicationControlStartRuleItemFormValue,
} from '@nirvana/shared/dtos/types/policies/common';
import { GzInputWebComponent } from '../../../../../../../../ui/gzInput/component/GzInputWebComponent';
import { RequiredValidator } from '../../../../../../../../ui/gzForm/validators/required.validator';
import { FormGroup } from '../../../../../../../../ui/gzForm/formGroup/form-group';
import { MaxLengthValidator } from '../../../../../../../../ui/gzForm/validators/max-length.validator';
import { GzInputNumberWebComponent, InputWidthValues } from '../../../../../../../../ui/gzInput';
import { GzCheckboxWebComponent } from '../../../../../../../../ui/gzCheckbox';
import { GzPolicyFullPageWindowFormWebComponent } from '../../../../../../../common/policyPanel/uiSettings/dialogs/fullPage/form';
import { GzApplicationControlTargetsTableWebComponent } from '../gridComponents/GzApplicationControlTargetsTableWebComponent';
import { getTargetTypeFormOptions } from '../../../../../utils/uiSettings/applicationControl/application-control.values';
import { GzDropdownWebComponent } from '../../../../../../../../ui/gzDropdown';
import { toggleVisibility } from '../../../../../../../../utils/htmlElement.utils';
import { GzComplexEnumListWebComponent } from '../../../../../../../../ui/gzComplexEnumList';
import { GzChipsGeneratorInputWebComponent } from '../../../../../../../../ui/gzChipsGeneratorInput';
import { MinLengthValidator } from '../../../../../../../../ui/gzForm/validators/min-length.validator';
import {
  convertAppInventoryRpcResponseDbValueToUiValue,
  convertAppInventoryPolicyDbValueToUiValue,
  convertAppInventoryUiValueToPolicyDbValue,
  convertRuleTargetsFormValueBySectionToDbValue,
} from '../utils/converters';
import {
  ApplicationInventoryApiClients,
  PolicyDependenciesContainer,
} from '../../../../../../../common/policyPanel/uiSettings/dependencies/PolicyDependenciesContainer';
import { debounce } from '../../../../../../../../utils/event.utils';

@customElement(WEB_COMPONENT_POLICY_PANEL_APPLICATION_CONTROL_RULES_FORM)
export class GzApplicationControlRulesFormWebComponent extends GzPolicyFullPageWindowFormWebComponent<ApplicationControlStartRuleItemFormValue> {
  // general section
  @query('#name')
  ruleName: GzFormFieldWebComponent<GzInputWebComponent, string>;
  @query('#priority')
  rulePriority: GzFormFieldWebComponent<GzInputNumberWebComponent, number>;
  @query('#active-form-field')
  private activeFormField: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;

  // targets section
  @query('#targets-type-field')
  private targetsTypeFormField: GzFormFieldWebComponent<GzDropdownWebComponent>;
  @query('#targets-processes-field')
  private targetsProcessesFormField: GzFormFieldWebComponent<
    GzApplicationControlTargetsTableWebComponent,
    ApplicationControlRuleTargetsProcesses[]
  >;
  @query('#targets-applications-section')
  private targetsApplicationsSection: HTMLElement;
  @query('#targets-applications-list')
  private targetsApplicationsList: GzComplexEnumListWebComponent;
  @query('#targets-applications-chips-generator-input')
  private targetsApplicationsFormField: GzFormFieldWebComponent<GzChipsGeneratorInputWebComponent, string[]>;
  @query('#include-subprocesses-field')
  private includeSubprocessesFormField: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;

  // exclusions section
  @query('#exclusions-type-field')
  private exclusionsTypeFormField: GzFormFieldWebComponent<GzDropdownWebComponent>;
  @query('#exclusions-processes-field')
  private exclusionsProcessesFormField: GzFormFieldWebComponent<
    GzApplicationControlTargetsTableWebComponent,
    ApplicationControlRuleTargetsProcesses[]
  >;
  @query('#exclusions-applications-section')
  private exclusionsApplicationsSection: HTMLElement;
  @query('#exclusions-applications-list')
  private exclusionsApplicationsList: GzComplexEnumListWebComponent;
  @query('#exclusions-applications-chips-generator-input')
  private exclusionsApplicationsFormField: GzFormFieldWebComponent<GzChipsGeneratorInputWebComponent, string[]>;

  // permissions section
  @query('#action-field')
  private actionFormField: GzFormFieldWebComponent<GzDropdownWebComponent>;

  private readonly maxPriority: number;
  private appInventorySearchString: string;
  private convertors = {};

  public constructor(maxPriority: number, value?: ApplicationControlStartRuleItemFormValue) {
    super(value);
    this.maxPriority = maxPriority;
  }

  static get styles() {
    return styles;
  }

  public setError(error: string) {
    this.ruleName.setError(error);
  }

  protected getConvertedFormValue() {
    const formValue = this.formGroup.value;

    convertRuleTargetsFormValueBySectionToDbValue(formValue, 'targets', this.convertors);
    convertRuleTargetsFormValueBySectionToDbValue(formValue, 'exclusions', this.convertors);

    return formValue;
  }

  createFormGroup(): void {
    this.ruleName.Validators = [
      RequiredValidator(translateFn('Required field')),
      MinLengthValidator(3, translateFn('The name must have a minimum of {minLength} characters.', { minLength: 3 })),
      MaxLengthValidator(
        128,
        translateFn('The name must have a maximum of {maxLength} characters.', { maxLength: 128 })
      ),
    ];
    this.targetsTypeFormField.Validators = [RequiredValidator(translateFn('Required field'))];
    this.targetsProcessesFormField.touched = true;
    this.targetsApplicationsFormField.touched = true;

    this.formGroup = FormGroup.build({
      name: this.ruleName,
      defaultPositionInGrid: this.rulePriority,
      active: this.activeFormField,
      action: this.actionFormField,
      targets: {
        type: this.targetsTypeFormField,
        processes: this.targetsProcessesFormField,
        appInventory: this.targetsApplicationsFormField,
      },
      includeSubprocesses: this.includeSubprocessesFormField,
      exclusions: {
        type: this.exclusionsTypeFormField,
        processes: this.exclusionsProcessesFormField,
        appInventory: this.exclusionsApplicationsFormField,
      },
    });
  }

  setFormGroupValue() {
    if (!this.value) {
      this.rulePriority.value = this.maxPriority;
      return;
    }

    const convertMethodForPinnedItems = item => {
      return { ...convertAppInventoryPolicyDbValueToUiValue(item), checked: true };
    };
    const convertMethodForAppInventoryValue = (val: ApplicationControlRuleTargetsAppInventory) => {
      this.convertors[val.name] = val;
      return val.name;
    };
    const convertMethodForProcessesValue = (val: ApplicationControlRuleTargetsProcesses) => {
      if (val.context === 0) {
        val.context = [];
      }
      return val;
    };

    this.targetsApplicationsList.pinnedItems = this.value.targets.appInventory?.map(convertMethodForPinnedItems) || [];
    this.exclusionsApplicationsList.pinnedItems =
      this.value.exclusions?.appInventory?.map(convertMethodForPinnedItems) || [];

    this.formGroup.value = {
      name: this.value.name,
      defaultPositionInGrid: this.value.defaultPositionInGrid,
      active: this.value.active,
      action: this.value.action,
      targets: {
        type: this.value.targets.type,
        processes: this.value.targets.processes?.map(convertMethodForProcessesValue) || [],
        appInventory: this.value.targets.appInventory?.map(convertMethodForAppInventoryValue) || [],
      },
      includeSubprocesses: this.value.includeSubprocesses,
      exclusions: {
        type: this.value.exclusions?.type || '',
        processes: this.value.exclusions?.processes?.map(convertMethodForProcessesValue) || [],
        appInventory: this.value.exclusions?.appInventory?.map(convertMethodForAppInventoryValue) || [],
      },
    };
  }

  /**
   * Render specific content.
   */
  protected renderContent() {
    this.updateComplete.then(() => {
      // set value to hide pinned items
      this.targetsApplicationsList.searchInput.value = 'N/A';
    });

    return html`
      <div class="rules-form-container">
        <gz-title fontWeight="500" fontSize="18" class="module-subtitle">${translateFn('General')}</gz-title>
        ${this.renderGeneralFormFields()}

        <gz-title fontWeight="500" fontSize="18" class="module-subtitle mt-5">${translateFn('Targets')}</gz-title>
        ${this.renderTargetsFormFields('targets')}

        <gz-form-field class="label-mw-250 checkbox-width" id="include-subprocesses-field">
          <gz-checkbox label="${translateFn('Include subprocesses')}" gz-form-input checked></gz-checkbox>
          <gz-text fontSize="12" fontWeight="400" class="helper-text"
            >${translateFn(
              'When setting rules for browser applications, it is recommended to deselect this option to prevent security risks.'
            )}
          </gz-text>
        </gz-form-field>

        <gz-title fontWeight="500" fontSize="18" class="module-subtitle mt-5">${translateFn('Exclusions')}</gz-title>
        ${this.renderTargetsFormFields('exclusions')}

        <gz-title fontWeight="500" fontSize="18" class="module-subtitle mt-5">${translateFn('Permissions')}</gz-title>
        ${this.renderPermissionsFormFields()}
      </div>
    `;
  }

  private renderGeneralFormFields(): TemplateResult {
    return html` <div class="rules-form w-full">
      <gz-form-field
        id="name"
        class="label-mw-250 mt-2"
        id="scan-actions-default-action-form-field"
        label="${translateFn('Rule name')}"
        required
      >
        <gz-input
          inputWidth="${InputWidthValues.LARGE}"
          gz-form-input
          placeholder="${translateFn('Enter a rule name')}"
        ></gz-input>
        <gz-input-error></gz-input-error>
      </gz-form-field>

      <gz-form-field id="priority" class="label-mw-250 mt-2" label="${translateFn('Set priority')}" required>
        <gz-input-number
          gz-form-input
          inputWidth="${InputWidthValues.SMALL}"
          min="${1}"
          max="${this.maxPriority}"
        ></gz-input-number>
        <gz-input-error></gz-input-error>
      </gz-form-field>

      <gz-form-field id="active-form-field" class="mt-2">
        <gz-checkbox gz-form-input label="${translateFn('Active')}" checked></gz-checkbox>
      </gz-form-field>
    </div>`;
  }

  private renderTargetsFormFields(id: string): TemplateResult {
    return html`
      <gz-form-field id="${id + '-type-field'}" label="${translateFn('Target')}" ?required="${id === 'targets'}">
        <gz-dropdown
          @selectionChanged="${e => this.handleTargetFieldChange(e, id)}"
          gz-form-input
          placeholder="${translateFn('Select target')}"
          options="${JSON.stringify(getTargetTypeFormOptions())}"
        ></gz-dropdown>
      </gz-form-field>

      <gz-form-field id="${id + '-processes-field'}" class="hidden">
        <gz-policy-panel-application-control-targets-table
          gz-form-input
        ></gz-policy-panel-application-control-targets-table>
        <gz-input-error></gz-input-error>
      </gz-form-field>

      <div id="${id + '-applications-section'}" class="hidden">
        <gz-form-field id="${id + '-applications-chips-generator-input'}">
          <gz-chips-generator-input
            gz-form-input
            themeInput
            manualAdd
            .strings="${{
              placeholder: translateFn('Search for applications or groups as you type'),
              label: translateFn('Applications'),
            }}"
            @inputChange="${(e: CustomEvent) => this.handleChipsGeneratorInputChange(e, id)}"
            @change="${(e: CustomEvent) => this.handleChipsGeneratorChange(e, id)}"
          ></gz-chips-generator-input>

          <gz-complex-enum-list
            class="hidden hidden-search-input"
            id="${id + '-applications-list'}"
            .noSearchResultsString="${translateFn('No results found')}"
            .errorMessageDetails="${{
              message: translateFn('A communication error has occurred. Please try again later.'),
            }}"
            @complexOptionChanged="${(e: CustomEvent) => this.handleComplexOptionChanged(e, id)}"
          ></gz-complex-enum-list>

          <gz-input-error></gz-input-error>
        </gz-form-field>
      </div>
    `;
  }

  private renderPermissionsFormFields(): TemplateResult {
    return html`
      <gz-labeled-content
        hasFormLabelWidth
        labelFontSize="13"
        labelFontWeight="400"
        label="${translateFn('Action for this rule') + ':'}"
      >
        <gz-form-field id="action-field" required>
          <gz-radio-group gz-form-input .selectedOption="${ApplicationControlRuleAction.ALLOW}">
            <gz-radio label="${translateFn('Allow')}" .value="${ApplicationControlRuleAction.ALLOW}"></gz-radio>
            <gz-radio label="${translateFn('Deny')}" .value="${ApplicationControlRuleAction.BLOCK}"></gz-radio>
          </gz-radio-group>
          <gz-input-error></gz-input-error>
        </gz-form-field>
      </gz-labeled-content>
    `;
  }

  private handleChipsGeneratorChange(event: CustomEvent, id: string): void {
    if (!event.detail.removedItem) {
      return;
    }

    // uncheck the item in the list
    const applicationsList = id === 'targets' ? this.targetsApplicationsList : this.exclusionsApplicationsList;
    applicationsList.pinnedItems = applicationsList.pinnedItems.filter(item => item.label !== event.detail.removedItem);
  }

  private handleComplexOptionChanged(event: CustomEvent, id: string): void {
    const applicationsField =
      id === 'targets' ? this.targetsApplicationsFormField : this.exclusionsApplicationsFormField;
    const applicationsList = id === 'targets' ? this.targetsApplicationsList : this.exclusionsApplicationsList;

    const pinnedItems = applicationsList.pinnedItems.filter(item => item.label !== event.detail.option.label);
    let value = applicationsField.value;

    if (event.detail.checked) {
      // add item to conversions and value
      this.convertors[event.detail.option.label] = convertAppInventoryUiValueToPolicyDbValue(event.detail.option);
      value = [...value, event.detail.option.label];

      // add item to pinnedItems
      applicationsList.pinnedItems = [
        ...pinnedItems,
        {
          label: event.detail.option.label,
          value: event.detail.option.value,
          checked: true,
        },
      ];
    } else {
      // remove item from conversions and value
      delete this.convertors[event.detail.option.label];
      value = value.filter(chip => chip !== event.detail.option.label);

      // keep filtered array of pinnedItems
      applicationsList.pinnedItems = pinnedItems;
    }

    applicationsField.value = value;
    applicationsField.Input.handleScrollToInput();

    applicationsField.requestUpdate();
  }

  private async handleChipsGeneratorInputChange(event: CustomEvent, id: string) {
    const applicationsList = id === 'targets' ? this.targetsApplicationsList : this.exclusionsApplicationsList;
    const clients = PolicyDependenciesContainer.getInstance().resolveDependency(
      'ApplicationInventory'
    ) as ApplicationInventoryApiClients;

    // save the search string to make sure we are not processing old search results
    this.appInventorySearchString = event.detail.value;
    if (!this.appInventorySearchString) {
      toggleVisibility(applicationsList, false);
      return;
    }

    toggleVisibility(applicationsList, true);
    applicationsList.isError = false;
    applicationsList.isLoading = true;

    // update list with new applications (wait for user to stop typing)
    debounce(async () => {
      // make backend call only for the latest search string
      if (event.detail.value !== this.appInventorySearchString) {
        return;
      }

      try {
        const response = await clients.GetApplicationOrGroupByName(this.appInventorySearchString);
        const applications = response.data;
        applicationsList.suggestedItems = applications.map(convertAppInventoryRpcResponseDbValueToUiValue);
      } catch (error) {
        applicationsList.isError = true;
      } finally {
        applicationsList.isLoading = false;
      }
    }, 700)();
  }

  private handleTargetFieldChange(event: CustomEvent, id: string): void {
    const processesFormField = id === 'targets' ? this.targetsProcessesFormField : this.exclusionsProcessesFormField;
    const applicationFormField =
      id === 'targets' ? this.targetsApplicationsFormField : this.exclusionsApplicationsFormField;
    const applicationsSection = id === 'targets' ? this.targetsApplicationsSection : this.exclusionsApplicationsSection;

    switch (event.detail.value) {
      case ApplicationControlRuleTargetsType.SPECIFIC_PROCESSES:
        toggleVisibility(processesFormField, true);
        toggleVisibility(applicationsSection, false);

        if (id === 'targets') {
          processesFormField.Validators = [RequiredValidator(translateFn('Required field'))];
          applicationFormField.Validators = [];
        }
        break;
      case ApplicationControlRuleTargetsType.APPLICATIONS_INVENTORY:
        toggleVisibility(processesFormField, false);
        toggleVisibility(applicationsSection, true);

        if (id === 'targets') {
          processesFormField.Validators = [];
          applicationFormField.Validators = [RequiredValidator(translateFn('Required field'))];
        }
        break;
    }
  }
}
